import React, { useState, useEffect } from 'react'
import { Modal } from 'react-bootstrap'
import Button from 'design-system/button'
import { Translation, useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useWindowSize } from 'hooks/react.hooks'
import { mobileWidth } from 'enum/responsive-width.enum'
import { ButtonSizes, ButtonVariant } from 'design-system/button/src/Button'
import { Actions } from 'store/actions/requestOutsideCourses.action'
import { prefix as prefixBase } from '../../constants'
import { FormRequestCourse, RequestNewCourseForm } from './FormRequestCourse'
import { Fields, Validations, Validation } from './FormRequestCourse'
import styles from './RequestNewCourseModal.module.scss'

interface RequestNewCourseModalProps {
  show: boolean
  onClose: () => void
  onSuccess?(): void
}

const getDefaultValues = (): RequestNewCourseForm => ({
  name: '',
  startDate: undefined,
  endDate: undefined,
  hours: null,
  sence_code: '',
  cost: '',
  link: '',
  message: '',
})

const commonValidations: Validation<any>[] = [
  (value) =>
    !value ? (
      <Translation>
        {(t) => t(`${prefixBase}.validations.required`)}
      </Translation>
    ) : (
      false
    ),
]

const validations: Validations = {
  name: commonValidations,
  message: commonValidations,
}

export const RequestNewCourseModal = ({
  show,
  onClose,
  onSuccess,
}: RequestNewCourseModalProps) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [values, setValues] = useState(() => getDefaultValues())
  const [onDesktop, setOnDesktop] = useState(true)
  const windowsSize = useWindowSize()

  useEffect(() => {
    if (windowsSize.width)
      setOnDesktop(windowsSize.width < mobileWidth ? false : true)
  }, [windowsSize])

  const handleClose = () => {
    onClose()
  }
  const handleSave = () => {
    const { hours } = values
    const startDate = values[Fields.startDate]?.toJSON()
    const endDate = values[Fields.endDate]?.toJSON()
    if (startDate && endDate && hours)
      dispatch(
        Actions.create({
          params: {
            name: values[Fields.name],
            cost: values[Fields.cost],
            startDate,
            endDate,
            hour: hours,
            message: values[Fields.message],
            senceCode: values[Fields.sence_code],
            urlCourse: values[Fields.link],
          },
          onSuccess: () => {
            onSuccess?.()
            handleClose()
            setValues(getDefaultValues())
          },
        })
      )
  }

  const valid = React.useMemo(() => {
    const keys = Object.values(Fields)
    return !keys.some((fieldName) => {
      const value = values[fieldName]
      const validationsField = validations[fieldName]
      if (validationsField)
        return validationsField.some((validation: any) => validation(value))
      return false
    })
  }, [values])

  const renderFooterButtons = () => {
    if (onDesktop) {
      return (
        <>
          <Button
            size={ButtonSizes.Small}
            variant={ButtonVariant.Secondary}
            onClick={handleClose}
          >
            {t('commons.buttons.cancel')}
          </Button>
          <Button
            size={ButtonSizes.Small}
            onClick={handleSave}
            disabled={!valid}
          >
            {t(`${prefixBase}.buttons.request`)}
          </Button>
        </>
      )
    }
    return (
      <>
        <Button
          size={ButtonSizes.Small}
          onClick={handleSave}
          disabled={!valid}
          block
          className={`${!onDesktop && 'mb-1 ml-0 mr-0'}`}
        >
          {t(`${prefixBase}.buttons.request`)}
        </Button>
        <Button
          size={ButtonSizes.Small}
          variant={ButtonVariant.Secondary}
          onClick={handleClose}
          block
          className={`${!onDesktop && 'mt-1 ml-0 mr-0'}`}
        >
          {t('commons.buttons.cancel')}
        </Button>
      </>
    )
  }
  return (
    <Modal
      show={show}
      size="lg"
      onHide={handleClose}
      centered
      className={styles.modalContainer}
    >
      <Modal.Header closeButton className="p-0 pb-2">
        <Modal.Title className={styles.title}>
          {t(`${prefixBase}.title`)}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 pt-2">
        <FormRequestCourse
          values={values}
          setValues={(newValues) => setValues(newValues)}
          validations={validations}
        />
      </Modal.Body>
      <Modal.Footer className="pl-0 pr-0">{renderFooterButtons()}</Modal.Footer>
    </Modal>
  )
}
